import React from 'react';
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/20/solid';

const pageSizeOptions = [10, 20, 50, 100];

/* eslint-disable react/prop-types */
/**
 * @template {import('@tanstack/react-table').RowData} TData
 * @param {{ table: import('@tanstack/react-table').Table<TData>, loading: boolean }} props
 */
const DataTablePagination = ({ table, loading }) => {
  const { pageIndex, pageSize } = table.getState().pagination;
  const availablePageSizes = [...new Set([...pageSizeOptions, pageSize])].sort((a, b) => a - b);
  const pageCount = table.getPageCount();
  const hasKnownPageCount = pageCount !== -1;
  const totalRows = table.getRowCount();
  if (totalRows === 0) return null;

  const firstRow = pageIndex * pageSize + 1;
  const lastRow = Math.min((pageIndex + 1) * pageSize, totalRows);

  return (
    <nav
      aria-label="Table pagination"
      className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-1 py-3"
    >
      <p className="text-sm p-1.5 text-gray-500" aria-live="polite">
        {firstRow}–{lastRow} of {totalRows}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <span>Rows per page</span>
          <select
            value={pageSize}
            onChange={(event) => table.setPageSize(Number(event.target.value))}
            disabled={loading}
            className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          >
            {availablePageSizes.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage() || loading}
            aria-label="First page"
            className="rounded p-1.5 text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronDoubleLeftIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage() || loading}
            aria-label="Previous page"
            className="rounded p-1.5 text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <span className="min-w-20 text-center text-sm text-gray-600" aria-live="polite">
            {hasKnownPageCount
              ? `Page ${pageCount === 0 ? 0 : pageIndex + 1} of ${pageCount}`
              : `Page ${pageIndex + 1}`}
          </span>
          <button
            type="button"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage() || loading}
            aria-label="Next page"
            className="rounded p-1.5 text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => table.setPageIndex(pageCount - 1)}
            disabled={!hasKnownPageCount || !table.getCanNextPage() || loading}
            aria-label="Last page"
            className="rounded p-1.5 text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronDoubleRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </nav>
  );
};

export default DataTablePagination;
